import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiFetch } from '../../utils/apiInterceptor';
import { API_BASE_URL } from '../../utils/paths';
import { FORECAST_TTL_MS } from '../../utils/forecastCache';

export const fetchAlertPreferences = createAsyncThunk(
  'alertPreferences/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/alert-preferences`, {
        method: 'GET',
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const updateAlertPreferences = createAsyncThunk(
  'alertPreferences/update',
  async (preferencesData, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/alert-preferences`, {
        method: 'PUT',
        body: JSON.stringify(preferencesData),
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  }
);

export const fetchAlertReport = createAsyncThunk(
  'alertPreferences/fetchReport',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiFetch(`${API_BASE_URL}/alert-preferences/report`, {
        method: 'GET',
      });

      const data = await response.json();

      if (!response.ok) {
        return rejectWithValue(data);
      }

      return data;
    } catch (error) {
      return rejectWithValue({ message: error.message });
    }
  },
  {
    // Pass { force: true } (Refresh button) to bypass the hourly reuse
    condition: ({ force = false } = {}, { getState }) => {
      const { reportFetchedAt } = getState().alertPreferences;
      return force || !reportFetchedAt || Date.now() - reportFetchedAt >= FORECAST_TTL_MS;
    },
  }
);

const alertPreferencesSlice = createSlice({
    name: 'alertPreferences',
    initialState: {
        preferences: null,
        isLoading: false,
        isSaving: false,
        errors: null,
        report: null,
        reportFetchedAt: null,
        isLoadingReport: false,
        reportErrors: null,
    },
    reducers: {
      clearErrors: (state) => {
        state.errors = null;
      },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchAlertPreferences.pending, (state) => {
                state.isLoading = true;
                state.errors = null;
            })
            .addCase(fetchAlertPreferences.fulfilled, (state, action) => {
                state.isLoading = false;
                state.preferences = action.payload;
            })
            .addCase(fetchAlertPreferences.rejected, (state, action) => {
                state.isLoading = false;
                state.errors = action.payload || { message: 'Failed to fetch alert preferences' };
            })
            .addCase(updateAlertPreferences.pending, (state) => {
                state.isSaving = true;
                state.errors = null;
            })
            .addCase(updateAlertPreferences.fulfilled, (state, action) => {
                state.isSaving = false;
                state.preferences = action.payload;
                // The report depends on these settings, so refetch it next time
                state.reportFetchedAt = null;
            })
            .addCase(updateAlertPreferences.rejected, (state, action) => {
                state.isSaving = false;
                state.errors = action.payload || { message: 'Failed to update alert preferences' };
            })
            .addCase(fetchAlertReport.pending, (state) => {
                state.isLoadingReport = true;
                state.reportErrors = null;
            })
            .addCase(fetchAlertReport.fulfilled, (state, action) => {
                state.isLoadingReport = false;
                state.report = action.payload;
                state.reportFetchedAt = Date.now();
            })
            .addCase(fetchAlertReport.rejected, (state, action) => {
                state.isLoadingReport = false;
                state.reportErrors = action.payload || { message: 'Failed to load the latest report' };
            });
    }
});

export const { clearErrors } = alertPreferencesSlice.actions;
export default alertPreferencesSlice.reducer;
